export const OUTLET_TYPES = ['store', 'central_kitchen', 'warehouse'] as const
export type OutletType = (typeof OUTLET_TYPES)[number]

/** Cabang milik tenant (readme-BE: Outlet). */
export interface Outlet {
  readonly id: string
  readonly name: string
  readonly type?: OutletType
  readonly active: boolean
  readonly address?: string
  readonly createdAt?: string
}

export interface CreateOutletInput {
  tenant_id: string
  name: string
  type?: OutletType
  address?: string
  timezone?: string
  currency?: string
  business_day_cutoff?: number
}

export const TAX_TYPES = [
  'inclusive_after_discount',
  'inclusive_before_discount',
  'exclusive_after_discount',
  'exclusive_before_discount',
] as const
export type TaxType = (typeof TAX_TYPES)[number]

export const SERVICE_CHARGE_MODES = [
  'service_charge_is_taxed',
  'service_charge_is_taxed_from_subtotal',
  'service_free_tax',
  'tax_is_service_charge',
] as const
export type ServiceChargeMode = (typeof SERVICE_CHARGE_MODES)[number]

export const ROUNDING_MODES = ['nearest', 'down', 'up'] as const
export type RoundingMode = (typeof ROUNDING_MODES)[number]

/**
 * Pengaturan uang milik outlet. Hanya `GET /outlets/:id` yang membawanya —
 * list sengaja tidak dipetakan ke sini supaya kode yang membutuhkan tarif
 * pajak ketahuan saat compile, bukan diam-diam menghitung dengan 0%.
 *
 * Layar kasir WAJIB memakai angka ini: BE adalah pencatat, bukan kalkulator —
 * ia menyimpan total kiriman frontend apa adanya dan hanya menandai
 * `totals_inconsistent` kalau meleset dari hitungannya sendiri.
 */
export interface OutletDetail extends Outlet {
  readonly taxType?: TaxType
  readonly serviceChargeMode?: ServiceChargeMode
  /** True = harga menu sudah mengandung pajak & service; grand total tidak bertambah. */
  readonly taxInclusive: boolean
  /** Persen bulat, `11` berarti 11%. */
  readonly taxValue: number
  readonly serviceValue: number
  /** Saklar terpisah dari kelipatannya: unit tetap tersimpan saat pembulatan dimatikan. */
  readonly roundingEnabled: boolean
  readonly roundingUnit: number
  readonly roundingMode: RoundingMode
  readonly address?: string
  readonly picture?: string
  readonly timezone?: string
  readonly businessDayCutoff?: number
  readonly kitchenEnabled?: boolean
  readonly voidDefaultPrepared?: boolean
  readonly invoicePrefix?: string
  readonly receiptHeader?: string
  readonly receiptFooter?: string
  readonly receiptShowLogo?: boolean
  /** ISO 4217 currency code, e.g. 'IDR', 'MYR', 'SGD' */
  readonly currency?: string
  readonly enableTableQRStatic?: boolean
  readonly enableTableQRDynamic?: boolean
}

export interface UpdateOutletInput {
  name: string
  type?: OutletType
  address?: string
  picture?: string
  taxType: TaxType
  serviceChargeMode?: ServiceChargeMode
  taxInclusive?: boolean
  taxValue: number
  serviceValue: number
  active?: boolean
  timezone?: string
  businessDayCutoff?: number
  kitchenEnabled?: boolean
  voidDefaultPrepared?: boolean
  invoicePrefix?: string
  roundingEnabled?: boolean
  roundingUnit?: number
  roundingMode?: RoundingMode
  receiptHeader?: string
  receiptFooter?: string
  receiptShowLogo?: boolean
  /** ISO 4217 currency code, e.g. 'IDR', 'MYR', 'SGD' */
  currency?: string
  enableTableQRStatic?: boolean
  enableTableQRDynamic?: boolean
}
